import { Form, Switch, Tooltip } from "antd";
import Img from "assets/images";
import CustomModal from "components/CustomModal";
import TextAlignLeft from "components/TextAlignLeft";
import ModalUpdateAddress from "features/Agent/components/ModalAddress/ModalUpdateAddress/ModalUpdateAddress";
import { truncateAddDots } from "features/WarrantyRequest/helperFuncs";
import {
  useDeleteSellerContact,
  useGetSellerContact,
  useSellerPermissions,
  useUpdateSellerContact,
} from "hooks/seller";
import { useSupplierPermissions } from "hooks/supplier";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { debounce, formatFullAddress, notify } from "utils/helperFuncs";

function useAddressBook({ isSupplier }) {
  const { t } = useTranslation();
  const { id } = useParams();

  const [switchClose, setSwitchClose] = useState(true);
  const [query, setQuery] = useState(null);
  const [approveAddressVisible, setApproveAddressVisible] = useState(false);
  const [approveContactRecord, setApproveContactRecord] = useState(null);
  const [approveAddressForm] = Form.useForm();

  const { sellerContacts: initialContacts, refetch, loading: fetching } = useGetSellerContact({ sellerID: id, query });
  const [sellerContacts, setSellerContacts] = useState([]);

  useEffect(() => {
    setSellerContacts(initialContacts || []);
  }, [initialContacts]);
  const { handleUpdateContact } = useUpdateSellerContact();
  const { handleDeleteContact } = useDeleteSellerContact();
  const { canUpdateContact, canDeleteContact } = useSellerPermissions();
  const { canSupplierContactUpdate, canSupplierContactDelete } = useSupplierPermissions();

  const canUpdateContactAll = isSupplier ? canSupplierContactUpdate : canUpdateContact;

  // Initial values for approve address form
  const approveAddressInitialValues = approveContactRecord
    ? {
        ...approveContactRecord,
        cityID: approveContactRecord?.ward?.version === "V1" ? null : approveContactRecord?.cityID,
        wardID: approveContactRecord?.ward?.version === "V1" ? null : approveContactRecord?.wardID,
      }
    : {};

  const handleApproveAddressValuesChange = async (changedValues, allValues) => {
    const values = await approveAddressForm.getFieldsValue();
    if (changedValues?.cityID) {
      approveAddressForm.setFieldsValue({
        ...values,
        wardID: null,
      });
    }
  };

  const handleOpenApproveAddress = (record) => {
    setApproveContactRecord(record);
    approveAddressForm.setFieldsValue({
      ...record,
      cityID: record?.ward?.version === "V1" ? null : record?.cityID,
      wardID: record?.ward?.version === "V1" ? null : record?.wardID,
    });
    setApproveAddressVisible(true);
  };

  const handleApproveAddress = async () => {
    try {
      const values = await approveAddressForm.validateFields();
      await handleUpdateContact({
        id: approveContactRecord?.id,
        contact: {
          ...values,
          email: values?.email || null,
          tel2: values?.tel2 || null,
          status: "ACTIVE",
          sellerID: id,
        },
      });
      notify.success({
        message: t("seller.componentAddressList.messageApproveAddressSuccess"),
      });
      setApproveAddressVisible(false);
      setSellerContacts((prev) =>
        prev.map((item) => (item.id === approveContactRecord?.id ? { ...item, status: "ACTIVE" } : item))
      );
    } catch (err) {
      notify.error({
        message: t("seller.componentAddressList.messageApproveAddressError"),
        description: err?.message,
      });
    }
  };

  const columns = [
    {
      title: t("common.index"),
      dataIndex: "no",
    },
    {
      title: t("seller.componentAddressList.fullName"),
      dataIndex: "fullName",
    },
    {
      title: t("seller.componentAddressList.address"),
      dataIndex: "address",
      render: (_, record) => {
        const fullAddress = formatFullAddress({
          address: record?.address,
          ward: record?.ward?.name,
          district: record?.district?.name,
          city: record?.city?.name,
        });
        return (
          <Tooltip title={fullAddress}>
            <TextAlignLeft value={truncateAddDots(fullAddress, 200)} />
          </Tooltip>
        );
      },
    },
    {
      title: t("seller.componentAddressList.telephone"),
      dataIndex: "telephone",
      render: (telephone, record) => (
        <div>
          <div>{telephone}</div>
          {record?.tel2 && (
            <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
              {record.tel2}
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>{"Liên hệ dự phòng"}</div>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      center: true,
      render: (status) => {
        const isActive = status === "ACTIVE";
        return (
          <span
            style={{
              padding: "4px 12px",
              borderRadius: "16px",
              backgroundColor: isActive ? "rgba(82, 196, 26, 0.15)" : "rgba(255, 77, 79, 0.15)",
              border: `1px solid ${isActive ? "rgba(82, 196, 26, 0.5)" : "rgba(255, 77, 79, 0.5)"}`,
              color: isActive ? "#389e0d" : "#cf1322",
              fontSize: "12px",
              fontWeight: 500,
            }}
          >
            {isActive ? "Đã duyệt" : "Đang chờ duyệt"}
          </span>
        );
      },
    },
    {
      title: t("seller.componentAddressList.actions"),
      dataIndex: "actions",
      align: "center",
      width: 200,
      render: (_, record) => {
        return (
          <div className="actions" style={{ display: "flex", justifyContent: "center", gap: "8px" }}>
            {(isSupplier ? canSupplierContactUpdate : canUpdateContact) && (
              <ModalUpdateAddress sellerID={id} refetch={refetch} contact={record} />
            )}

            {record?.status !== "ACTIVE" && (isSupplier ? canSupplierContactUpdate : canUpdateContact) && (
              <a className="approve-action" onClick={() => handleOpenApproveAddress(record)}>
                {t("seller.componentAddressList.approveAddress")}
              </a>
            )}

            {!record?.isDefault && (isSupplier ? canSupplierContactDelete : canDeleteContact) && (
              <CustomModal
                {...{
                  message: t("seller.componentAddressList.messageDeleteAddress"),
                  onOke: () => deleteContact(record?.id),
                  okeButtonLabel: t("common.confirm"),
                  customComponent: <a className="delete-action">{t("common.delete")}</a>,
                  centered: true,
                  footer: false,
                  width: "fit-content",
                  switchClose,
                  afterClose: refetch,
                  icon: Img.DeleteIcon,
                  closeOnOke: true,
                }}
              />
            )}
          </div>
        );
      },
    },
    {
      title: "Đặt làm mặc định",
      dataIndex: "isDefault",
      align: "center",
      render: (isDefault, record) => (
        <Switch
          checked={isDefault}
          onChange={() => !isDefault && setAsDefaultContact(record)}
          checkedChildren="ON"
          unCheckedChildren="OFF"
        />
      ),
    },
  ];

  async function deleteContact(addressId) {
    try {
      await handleDeleteContact({
        id: addressId,
      });
      notify.success({
        message: t("seller.componentAddressList.messageDeleteAddressSuccess"),
      });
    } catch (err) {
      notify.error({
        message: t("seller.componentAddressList.messageDeleteAddressError"),
        description: err?.message,
      });
    }
  }

  //Filter role by text
  const filterAddressByTextDebounced = debounce(filterAddressByText, 400);

  function filterAddressByText([text]) {
    if (text[0] === " ") {
      return;
    }
    setQuery(text);
  }

  async function setAsDefaultContact(record) {
    const { address, cityID, fullName, telephone, wardID, status } = record;
    try {
      await handleUpdateContact({
        id: record?.id,
        contact: {
          address,
          cityID,
          fullName,
          telephone,
          wardID,
          isDefault: true,
          status,
          sellerID: id,
        },
      });
      notify.success({
        message: t("seller.componentAddressList.messageSetupAddressDefaultSuccess"),
      });
      setSellerContacts((prev) =>
        prev.map((item) => (item.id === record.id ? { ...item, isDefault: true } : { ...item, isDefault: false }))
      );
    } catch (err) {
      notify.error({
        message: t("seller.componentAddressList.messageSetupAddressDefaultError"),
      });
    }
  }
  return {
    filterAddressByTextDebounced,
    id,
    columns,
    sellerContacts,
    fetching,
    refetch,
    canUpdateContactAll,
    approveAddressVisible,
    approveAddressForm,
    approveAddressInitialValues,
    handleApproveAddressValuesChange,
    handleApproveAddress,
    handleOpenApproveAddress,
    setApproveAddressVisible,
  };
}

export default useAddressBook;
